import { distributionFromAccounting } from './AccountingEditor';
import { percentStored } from './accountantModel';
import { documentFromState } from './formulaShape';

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function slug(label) {
  const key = String(label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return key || 'calculated_amount';
}

function typeLabel(row) {
  return row?.cost_type || row?.abbreviation || row?.name || '';
}

function cleanPhrase(value) {
  return String(value || '').replace(/\b(all|the|a|an|to|for|every|of)\b/g, ' ').replace(/\s+/g, ' ').trim();
}

function bestMatch(rows, phrase, fields) {
  const wanted = String(phrase || '').toLowerCase().trim();
  if (!wanted) return null;
  let best = null;
  let score = 0;
  (rows || []).forEach((row) => {
    fields(row).map((item) => String(item || '').toLowerCase().trim()).filter(Boolean).forEach((name) => {
      let next = 0;
      if (name === wanted) next = 100;
      else if (name.startsWith(wanted)) next = 80;
      else if (name.includes(wanted)) next = 60;
      else if (wanted.includes(name) && name.length > 3) next = 40;
      if (next > score) {
        score = next;
        best = row;
      }
    });
  });
  return best;
}

function costTypePhrase(text) {
  const coded = text.match(/(?:coded|code)\s+to\s+(?:a |an |the )?([a-z0-9][a-z0-9 /-]*?)\s+cost type/);
  if (coded) return cleanPhrase(coded[1]);
  const directed = text.match(/\b(?:to|for|using)\s+(?:a |an |the )?([a-z0-9][a-z0-9 /-]*?)\s+cost type(?!\s+expense)/);
  if (directed) return cleanPhrase(directed[1]);
  return '';
}

function accountPhrase(text, side) {
  const found = text.match(new RegExp(`${side}\\s+(?:the\\s+|a\\s+|an\\s+)?(.+?)\\s+(?:gl\\s+)?accounts?`));
  if (!found) return '';
  return found[1].replace(/\b(gl|the|a|an|account)\b/g, ' ').replace(/\s+/g, ' ').trim();
}

const BASIS_CAPABILITIES = [
  {
    key: 'gross_owner_billing_period',
    phrases: ['gross owner billing', 'owner billing', 'owner billings', 'billed to the owner'],
  },
  {
    key: 'cost_this_period',
    phrases: ['project costs', 'cost this period', 'costs this period', 'all costs', 'costs coded', 'costs code', 'of costs'],
  },
  {
    key: 'current_contract_value',
    phrases: [
      'current project contract value',
      'current contract value',
      'contract value including approved change orders',
      'contract value',
      'contract amount',
      'approved change orders',
    ],
  },
  {
    key: 'total_commitment_amount',
    phrases: [
      'total commitment amount',
      'commitment amount',
      'commitment change orders',
      'commitments',
    ],
  },
  {
    key: 'revenue_this_period',
    phrases: [
      'revenue from this period',
      'revenue this period',
      'revenue for this period',
      'revenue for the period',
      'contract revenue',
      'earned revenue',
      'revenue recognized',
      'recognized revenue',
      'revenue',
    ],
  },
  {
    key: 'manual_amount',
    phrases: ['amount entered', 'entered each period', 'entered each month', 'enter an amount', 'enter the amount', 'manual amount', 'monthly amount', 'amount each period', 'amount each month'],
  },
];

const UNMAPPED_BASIS = /\b(billing|billings|billed|payroll|hours|retainage|wip|contract)\b/;
const BASIS_QUESTION = 'Should this use project costs, owner billing, revenue from this period, current contract value, total commitment amount, or an amount entered each period?';

function matchBasis(text) {
  const lower = String(text || '').toLowerCase();
  const hits = BASIS_CAPABILITIES.filter((item) => item.phrases.some((phrase) => lower.includes(phrase)));
  let remainder = lower;
  hits.forEach((item) => {
    item.phrases.forEach((phrase) => {
      if (remainder.includes(phrase)) remainder = remainder.split(phrase).join(' ');
    });
  });
  if (hits.length === 1 && hits[0].key === 'gross_owner_billing_period') {
    remainder = remainder.replace(/\b(billing|billings|billed)\b/g, ' ');
  }
  const leftover = UNMAPPED_BASIS.test(remainder);
  if (hits.length > 1) return { ambiguous: true };
  if (hits.length === 1 && leftover) return { unrecognized: true };
  if (hits.length === 1) return { basis: hits[0].key };
  if (leftover) return { unrecognized: true };
  return {};
}

function emptySlots() {
  return {
    basis: '',
    rateValue: '',
    rateDisplay: '',
    costType: null,
    costTypeAll: false,
    costTypeKnown: false,
    policy: '',
    debitExpense: false,
    debitAccount: null,
    debitKnown: false,
    creditAccount: null,
    creditAccounts: [],
    rates: [],
    creditKnown: false,
    destination: null,
    destinationKnown: false,
    timing: '',
  };
}

const TIMING_QUESTION = 'Should this calculate every period, or only when the amount it is based on changes?';

function matchTiming(text) {
  const lower = cleanPhrase(text).toLowerCase().replace(/\bamount entered each period\b/g, ' ');
  if (/\bcumulat|\bcumalit/.test(lower)) return 'cumulative';
  if (/\bonly (recalculate|create|post|record)\b/.test(lower)) return 'cumulative';
  if (/\bonly when\b.*\bchanges?\b/.test(lower)) return 'cumulative';
  if (/\b(if|when) the amount\b.*\bchanges?\b/.test(lower)) return 'cumulative';
  if (/\beach period\b|\bevery period\b|\bevery month\b|\bperiodic\b/.test(lower)) return 'periodic';
  return '';
}

function timingSentence(timing) {
  if (timing === 'cumulative') return 'A new amount is recorded only when the amount it is based on changes.';
  if (timing === 'periodic') return 'It calculates every period.';
  return '';
}

const QUESTIONS = {
  basis: BASIS_QUESTION,
  rate: 'What percent should it apply?',
  cost_type: 'Which cost type should it use? You can also say all cost types.',
  policy: 'Should this create a project cost, or only a journal entry?',
  debit: 'Which account should it debit? You can name a GL account, or say the cost type expense account.',
  credit: 'Which GL account should it credit?',
  destination: 'Which cost code should the project cost use?',
  timing: TIMING_QUESTION,
};

export function askChoices(key, context = {}) {
  if (key === 'basis') {
    return {
      freeform: false,
      options: [
        { label: 'Project costs', answer: 'project costs' },
        { label: 'Owner billing', answer: 'owner billing' },
        { label: 'Revenue from this period', answer: 'revenue from this period' },
        { label: 'Current contract value', answer: 'current contract value' },
        { label: 'Total commitment amount', answer: 'total commitment amount' },
        { label: 'Amount entered each period', answer: 'an amount entered each period' },
      ],
    };
  }
  if (key === 'timing') {
    return {
      freeform: false,
      options: [
        { label: 'Every period', answer: 'every period' },
        { label: 'Only when the amount it is based on changes', answer: 'only when the amount it is based on changes' },
      ],
    };
  }
  if (key === 'policy') {
    return {
      freeform: false,
      options: [
        { label: 'Create a project cost', answer: 'create a project cost' },
        { label: 'Journal entry only', answer: 'journal entry only' },
        { label: 'Each project decides', answer: 'each project decides' },
      ],
    };
  }
  if (key === 'cost_type') {
    const options = (context.costTypes || []).map((row) => {
      const label = row.cost_type || row.abbreviation || row.name || '';
      return { label, answer: label };
    }).filter((row) => row.label);
    options.push({ label: 'All cost types', answer: 'all cost types' });
    return { freeform: false, options };
  }
  if (key === 'debit') {
    return {
      freeform: true,
      options: [{ label: 'Cost type expense account', answer: 'cost type expense account' }],
    };
  }
  if (key === 'destination') {
    return {
      freeform: true,
      options: [{ label: 'Choose later', answer: 'later', skip: true }],
    };
  }
  return { freeform: true, options: [] };
}

export function questionsFor(slots, context = {}) {
  const current = slots || emptySlots();
  const list = [];
  if (!current.basis) list.push({ key: 'basis', text: QUESTIONS.basis });
  else if (current.basis !== 'manual_amount' && !current.rateValue) list.push({ key: 'rate', text: QUESTIONS.rate });
  if (current.basis === 'cost_this_period' && !current.costTypeKnown) list.push({ key: 'cost_type', text: QUESTIONS.cost_type });
  const basisReady = current.basis && (current.basis === 'manual_amount' || current.rateValue);
  const sourceReady = current.basis !== 'cost_this_period' || current.costTypeKnown;
  if (basisReady && sourceReady && !current.timing) list.push({ key: 'timing', text: QUESTIONS.timing });
  if (!current.policy) list.push({ key: 'policy', text: QUESTIONS.policy });
  else if (current.policy === 'always' && destinationChoices(context.costCodes, current).length && !current.destinationKnown) {
    list.push({ key: 'destination', text: QUESTIONS.destination });
  }
  if (!current.debitKnown) list.push({ key: 'debit', text: QUESTIONS.debit });
  if (!current.creditKnown) list.push({ key: 'credit', text: QUESTIONS.credit });
  return list;
}

function destinationChoices(costCodes, slots) {
  const rows = costCodes || [];
  const typeVuid = slots?.costType?.vuid;
  if (!typeVuid) return rows;
  const matched = rows.filter((row) => row.cost_type_vuid === typeVuid);
  return matched.length ? matched : rows;
}

function matchDestination(costCodes, phrase, slots) {
  const wanted = cleanPhrase(phrase).toLowerCase();
  if (!wanted) return { unmatched: true };
  const scored = [];
  destinationChoices(costCodes, slots).forEach((row) => {
    const names = [row.code, row.description, `${row.code || ''} ${row.description || ''}`]
      .map((item) => String(item || '').toLowerCase().trim())
      .filter(Boolean);
    let score = 0;
    names.forEach((name) => {
      let next = 0;
      if (name === wanted) next = 100;
      else if (name.startsWith(wanted) || wanted.startsWith(name)) next = 80;
      else if (name.includes(wanted) || wanted.includes(name)) next = 60;
      if (next > score) score = next;
    });
    if (score) scored.push({ row, score });
  });
  const best = scored.reduce((highest, item) => Math.max(highest, item.score), 0);
  const winners = scored.filter((item) => item.score === best);
  if (!winners.length) return { unmatched: true };
  if (winners.length > 1) return { ambiguous: true };
  return { row: winners[0].row };
}

function mentionedDestination(text, costCodes) {
  const lower = String(text || '').toLowerCase();
  const explicit = lower.match(/\b(?:to|on|against|into|using)\s+(?:the\s+)?([a-z0-9][a-z0-9 ./-]*?)\s+cost codes?\b/)
    || lower.match(/\bcost codes?\s+([a-z0-9][a-z0-9 ./-]*?)(?:\.|,| and | using | with |$)/);
  if (explicit) {
    const found = matchDestination(costCodes, explicit[1], {});
    return found.row ? { destination: found.row } : {};
  }
  const hits = [];
  const seen = new Set();
  (costCodes || []).forEach((row) => {
    const names = [row.code, row.description]
      .map((item) => String(item || '').toLowerCase().trim())
      .filter((name) => name.length >= 4 && (name.includes(' ') || /\d/.test(name)));
    if (!names.some((name) => lower.includes(name))) return;
    const id = row.cost_code_vuid || row.key;
    if (seen.has(id)) return;
    seen.add(id);
    hits.push(row);
  });
  if (hits.length === 1) return { destination: hits[0] };
  return {};
}

function matchAccount(accounts, phrase) {
  const raw = cleanPhrase(phrase).toLowerCase();
  const exact = (accounts || []).find((row) => [row.account_name, row.name, row.account_number]
    .some((item) => String(item || '').toLowerCase().trim() === raw));
  if (exact) return exact;
  const cleaned = cleanPhrase(String(phrase || '').replace(/\b(gl|accounts?)\b/g, ' '));
  return bestMatch(accounts, cleaned, (row) => [row.account_name, row.name, row.account_number]);
}

function sentenceAt(text, index) {
  const before = text.slice(0, index);
  const start = Math.max(before.lastIndexOf('.'), before.lastIndexOf(';'), before.lastIndexOf('\n'));
  const rest = text.slice(index);
  const end = rest.search(/[.;\n]/);
  return text.slice(start + 1, end === -1 ? text.length : index + end).toLowerCase();
}

function mentionedCredits(text, accounts) {
  const pattern = /\bcredit(?:ed)?\s+(?:to\s+)?(?:the\s+|a\s+|an\s+)?([a-z0-9][a-z0-9 &'/-]*?)(?=\s+for\b|\s+and\b|\s+accounts?\b|[.,;]|$)/gi;
  const found = [];
  const seen = new Set();
  for (const match of String(text || '').matchAll(pattern)) {
    const account = matchAccount(accounts, match[1]);
    if (!account || seen.has(account.vuid)) continue;
    seen.add(account.vuid);
    const after = text.slice(match.index + match[0].length, match.index + match[0].length + 48).toLowerCase();
    let role = 'amount';
    if (/\bdifference\b/.test(after)) role = 'difference';
    else if (/\baccru/.test(after)) role = 'accrual';
    else {
      const sentence = sentenceAt(text, match.index);
      if (/\bdifference\b/.test(sentence)) role = 'difference';
      else if (/\baccru/.test(sentence)) role = 'accrual';
    }
    found.push({ account, role });
  }
  return found;
}

function mentionedRates(text) {
  const pattern = /\$?\s*(\d+(?:\.\d+)?)\s*(?:per|\/)\s*\$?\s*1[,.]?000/gi;
  const found = [];
  for (const match of String(text || '').matchAll(pattern)) {
    const before = text.slice(Math.max(0, match.index - 70), match.index).toLowerCase();
    found.push({
      value: match[1],
      display: 'per_thousand',
      role: /\baccru/.test(before) ? 'accrual' : 'charge',
    });
  }
  return found;
}

function matchCostType(costTypes, phrase) {
  const cleaned = cleanPhrase(String(phrase || '').replace(/\bcost type\b/g, ' '));
  return bestMatch(costTypes, cleaned, (row) => [row.cost_type, row.abbreviation, row.name]);
}

function readUtterance(text, { accounts = [], costTypes = [], costCodes = [] } = {}) {
  const raw = String(text || '').trim();
  const lower = raw.toLowerCase();
  const notes = [];
  const patch = {};
  if (!raw) return { patch, notes };
  const percent = raw.match(/(\d+(?:\.\d+)?)\s*(%|percent)/i);
  const perThousand = raw.match(/\$?\s*(\d+(?:\.\d+)?)\s*(?:per|\/)\s*\$?\s*1[,.]?000/i);
  const basis = matchBasis(lower);
  if (basis.basis) patch.basis = basis.basis;
  else if (basis.ambiguous) notes.push(`More than one basis matches. ${BASIS_QUESTION}`);
  else if (basis.unrecognized) notes.push('That amount is not one this calculation can use yet.');
  if (perThousand) {
    patch.rateValue = perThousand[1];
    patch.rateDisplay = 'per_thousand';
  } else if (percent) {
    patch.rateValue = percent[1];
    patch.rateDisplay = 'percent';
  }
  if (
    /journal(?: entry)? only|only (?:create )?(?:a )?journal entry/.test(lower)
    || /without (a )?project cost|\bno project cost\b/.test(lower)
    || /\b(?:not|never|don'?t|do not|does not|should not|shouldn't)\s+(?:be\s+)?(?:create\s+)?(?:a\s+)?project cost\b/.test(lower)
  ) patch.policy = 'never';
  else if (/let each project decide|each project decides/.test(lower)) patch.policy = 'project_choice';
  else if (/\bproject cost\b|add (it|this|the amount) to the project/.test(lower)) patch.policy = 'always';
  if (patch.policy !== 'never') {
    const found = mentionedDestination(raw, costCodes);
    if (found.destination) {
      patch.destination = found.destination;
      if (!patch.policy) patch.policy = 'always';
    }
  }
  if (/\ball cost types\b|\bevery cost type\b|\bany cost type\b/.test(lower)) patch.costTypeAll = true;
  const typePhrase = costTypePhrase(lower);
  if (typePhrase) {
    const sourceType = matchCostType(costTypes, typePhrase);
    if (sourceType) patch.costType = sourceType;
    else notes.push(`No cost type matched “${typePhrase}”.`);
  }
  if (/cost type expense|destination expense/.test(lower)) patch.debitExpense = true;
  const debitPhrase = patch.debitExpense ? '' : accountPhrase(lower, 'debit');
  if (debitPhrase) {
    const debitAccount = matchAccount(accounts, debitPhrase);
    if (debitAccount) patch.debitAccount = debitAccount;
    else notes.push(`No GL account matched “${debitPhrase}”.`);
  }
  const credits = mentionedCredits(raw, accounts);
  if (!credits.length) {
    const creditPhrase = accountPhrase(lower, 'credit');
    if (creditPhrase) {
      const creditAccount = matchAccount(accounts, creditPhrase);
      if (creditAccount) credits.push({ account: creditAccount, role: 'amount' });
      else notes.push(`No GL account matched “${creditPhrase}”.`);
    }
  }
  if (credits.length) {
    patch.creditAccounts = credits;
    patch.creditAccount = credits[0].account;
  }
  const rates = mentionedRates(raw);
  if (rates.length > 1) patch.rates = rates;
  const timing = matchTiming(lower);
  if (timing) patch.timing = timing;
  return { patch, notes };
}

function answerPending(pending, text, context = {}) {
  const { accounts = [], costTypes = [] } = context;
  const raw = String(text || '').trim();
  const lower = raw.toLowerCase();
  if (!pending || !raw) return { patch: {} };
  if (pending === 'basis') {
    const found = matchBasis(lower);
    if (found.basis) return { patch: { basis: found.basis } };
    if (/\bcommitment/.test(lower)) return { patch: { basis: 'total_commitment_amount' } };
    if (/\bcontract value\b|\bcontract amount\b|\bapproved change orders\b/.test(lower)) return { patch: { basis: 'current_contract_value' } };
    if (/\brevenue\b/.test(lower)) return { patch: { basis: 'revenue_this_period' } };
    if (/\bbilling\b/.test(lower)) return { patch: { basis: 'gross_owner_billing_period' } };
    if (/\bproject\b|\bcosts?\b/.test(lower)) return { patch: { basis: 'cost_this_period' } };
    if (/\bentered\b|\bmanual\b/.test(lower)) return { patch: { basis: 'manual_amount' } };
    return { unmatched: true, note: BASIS_QUESTION };
  }
  if (pending === 'rate') {
    const perThousand = raw.match(/\$?\s*(\d+(?:\.\d+)?)\s*(?:per|\/)\s*\$?\s*1[,.]?000/i);
    const number = raw.match(/(\d+(?:\.\d+)?)/);
    if (perThousand) return { patch: { rateValue: perThousand[1], rateDisplay: 'per_thousand' } };
    if (number) return { patch: { rateValue: number[1], rateDisplay: 'percent' } };
    return { unmatched: true, note: 'Enter the percent, such as 2%.' };
  }
  if (pending === 'cost_type') {
    const typed = costTypePhrase(lower);
    if (typed) {
      const named = matchCostType(costTypes, typed);
      if (named) return { patch: { costType: named } };
    }
    if (/^(all|all of them|everything|every( cost type)?|any( cost type)?|all cost types)\b/.test(lower)) return { patch: { costTypeAll: true } };
    const sourceType = matchCostType(costTypes, raw);
    if (sourceType) return { patch: { costType: sourceType } };
    return { unmatched: true, note: `No cost type matched “${raw}”. Try the cost type name, or say all cost types.` };
  }
  if (pending === 'timing') {
    const found = matchTiming(lower);
    if (found) return { patch: { timing: found } };
    if (/\bchange/.test(lower)) return { patch: { timing: 'cumulative' } };
    if (/\bperiod\b|\bmonth\b/.test(lower)) return { patch: { timing: 'periodic' } };
    return { unmatched: true, note: TIMING_QUESTION };
  }
  if (pending === 'policy') {
    if (/journal|\bonly\b|\bno\b/.test(lower)) return { patch: { policy: 'never' } };
    if (/decide|each project/.test(lower)) return { patch: { policy: 'project_choice' } };
    if (/yes|create|project/.test(lower)) return { patch: { policy: 'always' } };
    return { unmatched: true, note: 'Say create a project cost, or journal entry only.' };
  }
  if (pending === 'debit') {
    if (/expense/.test(lower)) return { patch: { debitExpense: true } };
    const debitAccount = matchAccount(accounts, raw);
    if (debitAccount) return { patch: { debitAccount } };
    return { unmatched: true, note: `No GL account matched “${raw}”. You can also say the cost type expense account.` };
  }
  if (pending === 'credit') {
    const creditAccount = matchAccount(accounts, raw);
    if (creditAccount) return { patch: { creditAccount } };
    return { unmatched: true, note: `No GL account matched “${raw}”.` };
  }
  if (pending === 'destination') {
    if (/\b(later|not now|not yet|skip|leave it)\b/.test(lower)) {
      return { patch: { destinationLater: true }, note: 'You can set the cost code later.' };
    }
    const found = matchDestination(context.costCodes, raw, context.slots);
    if (found.row) return { patch: { destination: found.row } };
    if (found.ambiguous) return { patch: { destinationLater: true }, note: 'More than one cost code matches. You can set it later.' };
    return { patch: { destinationLater: true }, note: `No cost code matched “${raw}”. You can set it later.` };
  }
  return { patch: {} };
}

function mergeSlots(base, patch) {
  const next = { ...(base || emptySlots()) };
  if (!patch) return next;
  if (patch.basis) next.basis = patch.basis;
  if (patch.rateValue) {
    next.rateValue = patch.rateValue;
    next.rateDisplay = patch.rateDisplay || 'percent';
  }
  if (patch.costType) {
    next.costType = patch.costType;
    next.costTypeAll = false;
    next.costTypeKnown = true;
  }
  if (patch.costTypeAll) {
    next.costType = null;
    next.costTypeAll = true;
    next.costTypeKnown = true;
  }
  if (patch.timing) next.timing = patch.timing;
  if (patch.policy) next.policy = patch.policy;
  if (patch.debitExpense) {
    next.debitExpense = true;
    next.debitAccount = null;
    next.debitKnown = true;
  }
  if (patch.debitAccount) {
    next.debitAccount = patch.debitAccount;
    next.debitExpense = false;
    next.debitKnown = true;
  }
  if (patch.creditAccount) {
    next.creditAccount = patch.creditAccount;
    next.creditKnown = true;
  }
  if (patch.creditAccounts && patch.creditAccounts.length) {
    next.creditAccounts = patch.creditAccounts;
    next.creditAccount = patch.creditAccounts[0].account;
    next.creditKnown = true;
  }
  if (patch.rates && patch.rates.length) next.rates = patch.rates;
  if (patch.destination) {
    next.destination = patch.destination;
    next.destinationKnown = true;
  }
  if (patch.destinationLater) {
    next.destination = null;
    next.destinationKnown = true;
  }
  return next;
}

function rateParameter(key, label, value) {
  return {
    id: uid(),
    key,
    label,
    description: 'Each project can use its own rate. The default is used when the calculation is first assigned.',
    required: true,
    defaultValue: value,
    display: 'per_thousand',
  };
}

function basisChain(basis, parameterKey) {
  return {
    terms: [
      { kind: 'input', key: basis, value: '' },
      { kind: 'constant', key: '', value: '1000' },
      { kind: 'parameter', key: parameterKey, value: '' },
    ],
    operators: ['div', 'mul'],
  };
}

function creditLine(amountKey, account) {
  return {
    id: uid(),
    side: 'credit',
    amount_key: amountKey,
    strategy: 'configured_account',
    role_key: slug(account?.account_name || 'credit'),
    role_label: account?.account_name || '',
    account_vuid: account?.vuid || '',
    job_cost_policy: 'never',
    line_key: `${amountKey}-credit`,
    memo: '',
  };
}

function buildSplitDocument(current, name) {
  const basis = current.basis;
  const policy = current.policy || 'always';
  const chargeRate = current.rates.find((item) => item.role === 'charge') || current.rates[0];
  const accrualRate = current.rates.find((item) => item.role === 'accrual') || current.rates[1];
  const differenceCredit = current.creditAccounts.find((item) => item.role === 'difference') || current.creditAccounts[0];
  const accrualCredit = current.creditAccounts.find((item) => item.role === 'accrual') || current.creditAccounts.find((item) => item !== differenceCredit);
  const parameters = [
    rateParameter('charge_rate', 'Charge Rate', chargeRate.value),
    rateParameter('accrual_rate', 'Accrual Rate', accrualRate.value),
  ];
  const charge = { id: uid(), key: 'charge', label: 'Charge', ...basisChain(basis, 'charge_rate') };
  const accrual = { id: uid(), key: 'accrual', label: 'Accrual', ...basisChain(basis, 'accrual_rate') };
  const difference = {
    id: uid(),
    key: 'difference',
    label: 'Difference',
    terms: [
      { kind: 'named', key: 'charge', value: '' },
      { kind: 'named', key: 'accrual', value: '' },
    ],
    operators: ['sub'],
  };
  const debitStrategy = current.debitAccount ? 'configured_account' : 'cost_type_expense';
  const debit = {
    id: uid(),
    side: 'debit',
    amount_key: 'charge',
    strategy: debitStrategy,
    role_key: current.debitAccount ? slug(current.debitAccount.account_name || 'debit') : '',
    role_label: debitStrategy === 'cost_type_expense' ? 'Project destination expense account' : (current.debitAccount?.account_name || ''),
    account_vuid: debitStrategy === 'cost_type_expense' ? '' : (current.debitAccount?.vuid || ''),
    job_cost_policy: policy,
    line_key: 'charge-debit',
    memo: '',
  };
  const credits = [
    creditLine('difference', differenceCredit?.account),
    creditLine('accrual', accrualCredit?.account),
  ];
  const basisText = basis === 'gross_owner_billing_period'
    ? 'owner billing'
    : basis === 'revenue_this_period'
      ? 'revenue from this period'
      : basis === 'current_contract_value'
        ? 'current contract value'
        : basis === 'total_commitment_amount'
          ? 'the total commitment amount, including change orders'
          : 'project costs';
  const recorded = timingSentence(current.timing);
  const description = `$${chargeRate.value} per $1,000 charge and $${accrualRate.value} per $1,000 accrual of ${basisText}. Creates a project cost, debits the cost type expense account, credits ${differenceCredit?.account?.account_name || 'the difference'}, and credits ${accrualCredit?.account?.account_name || 'the accrual'}.${recorded ? ` ${recorded}` : ''}`;
  return {
    calculationType: current.timing === 'cumulative' ? 'cumulative' : 'periodic',
    description,
    parameters,
    namedValues: [charge, accrual, difference],
    accountingLines: [debit, ...credits],
    source: current.costType
      ? { cost_type_vuid: current.costType.vuid, cost_type_label: typeLabel(current.costType) }
      : { cost_type_vuid: '', cost_type_label: '' },
  };
}

function buildDocument(slots, name) {
  const current = slots || emptySlots();
  if (current.basis && current.basis !== 'manual_amount' && (current.rates || []).length > 1 && (current.creditAccounts || []).length > 1) {
    return buildSplitDocument(current, name);
  }
  const basis = current.basis;
  const policy = current.policy || 'always';
  const label = String(name || '').trim() || 'Calculated amount';
  const key = slug(label);
  const parameters = [];
  const terms = [];
  const operators = [];
  if (!basis) terms.push({ kind: '', key: '', value: '' });
  else if (basis === 'manual_amount') terms.push({ kind: 'input', key: 'manual_amount', value: '' });
  else {
    terms.push({ kind: 'input', key: basis, value: '' });
    if (current.rateValue) {
      const named = String(name || '').trim();
      parameters.push({
        id: uid(),
        key: 'rate',
        label: named ? `${named} Rate` : 'Rate',
        description: 'Each project can use its own rate. The default is used when the calculation is first assigned.',
        required: true,
        defaultValue: current.rateDisplay === 'per_thousand' ? current.rateValue : percentStored(current.rateValue),
        display: current.rateDisplay === 'per_thousand' ? 'per_thousand' : 'percent',
      });
      if (current.rateDisplay === 'per_thousand') {
        operators.push('div', 'mul');
        terms.push({ kind: 'constant', key: '', value: '1000' });
        terms.push({ kind: 'parameter', key: 'rate', value: '' });
      } else {
        operators.push('mul');
        terms.push({ kind: 'parameter', key: 'rate', value: '' });
      }
    }
  }
  const named = { id: uid(), key, label, terms, operators };
  const debitAccount = current.debitAccount;
  const debitStrategy = debitAccount
    ? 'configured_account'
    : (current.debitExpense || policy === 'always')
      ? 'cost_type_expense'
      : 'configured_account';
  const debit = {
    id: uid(),
    side: 'debit',
    amount_key: key,
    strategy: debitStrategy,
    role_key: debitAccount ? slug(debitAccount.account_name || 'debit') : '',
    role_label: debitStrategy === 'cost_type_expense' ? 'Project destination expense account' : (debitAccount?.account_name || ''),
    account_vuid: debitStrategy === 'cost_type_expense' ? '' : (debitAccount?.vuid || ''),
    job_cost_policy: policy,
    line_key: `${key}-debit`,
    memo: '',
  };
  const creditAccount = current.creditAccount;
  const credit = {
    id: uid(),
    side: 'credit',
    amount_key: key,
    strategy: 'configured_account',
    role_key: creditAccount ? slug(creditAccount.account_name || 'credit') : `${key}_offset`,
    role_label: creditAccount?.account_name || '',
    account_vuid: creditAccount?.vuid || '',
    job_cost_policy: 'never',
    line_key: `${key}-credit`,
    memo: '',
  };
  const basisText = basis === 'gross_owner_billing_period'
    ? 'owner billing'
    : basis === 'revenue_this_period'
      ? 'revenue from this period'
      : basis === 'current_contract_value'
        ? 'current contract value'
        : basis === 'total_commitment_amount'
          ? 'the total commitment amount, including change orders'
          : basis === 'manual_amount'
        ? 'the amount entered each period'
        : 'project costs';
  const rateText = current.rateValue
    ? (current.rateDisplay === 'per_thousand' ? `$${current.rateValue} per $1,000 of ` : `${current.rateValue}% of `)
    : '';
  const sourceText = current.costType ? ` coded to ${typeLabel(current.costType)}` : '';
  const costText = policy === 'always'
    ? 'Creates a project cost'
    : policy === 'never'
      ? 'Journal entry only'
      : 'Each project decides whether to create a project cost';
  const debitText = debitStrategy === 'cost_type_expense'
    ? 'debits the cost type expense account'
    : `debits ${debitAccount?.account_name || 'a GL account'}`;
  const creditText = creditAccount ? `credits ${creditAccount.account_name}` : 'needs a credit account';
  const recorded = timingSentence(current.timing);
  const description = !basis
    ? ''
    : basis === 'manual_amount'
      ? `${costText}, ${debitText}, and ${creditText}.${recorded ? ` ${recorded}` : ''}`
      : `${rateText}${basisText}${sourceText}. ${costText}, ${debitText}, and ${creditText}.${recorded ? ` ${recorded}` : ''}`;
  return {
    calculationType: current.timing === 'cumulative' ? 'cumulative' : 'periodic',
    description,
    parameters,
    namedValues: [named],
    accountingLines: [debit, credit],
    source: current.costType
      ? { cost_type_vuid: current.costType.vuid, cost_type_label: typeLabel(current.costType) }
      : { cost_type_vuid: '', cost_type_label: '' },
  };
}

export function absorb(slots, text, context = {}) {
  const raw = String(text || '').trim();
  if (!raw) {
    const questions = questionsFor(slots, context);
    return { empty: true, slots: slots || emptySlots(), notes: [], questions, question: questions[0] || null, draft: buildDocument(slots, context.name) };
  }
  const heard = readUtterance(raw, context);
  const pending = answerPending(context.pending, raw, { ...context, slots });
  let next = mergeSlots(slots, heard.patch);
  const heardAnswered = {
    basis: !!heard.patch.basis,
    rate: !!heard.patch.rateValue,
    cost_type: !!(heard.patch.costType || heard.patch.costTypeAll),
    timing: !!heard.patch.timing,
    policy: !!heard.patch.policy,
    debit: !!(heard.patch.debitExpense || heard.patch.debitAccount),
    credit: !!heard.patch.creditAccount,
    destination: !!heard.patch.destination,
  }[context.pending];
  if (!pending.unmatched && !heardAnswered) next = mergeSlots(next, pending.patch);
  const questions = questionsFor(next, context);
  const notes = [...heard.notes];
  if (pending.note && !heardAnswered) notes.push(pending.note);
  const reply = pending.note && !heardAnswered
    ? [pending.note, pending.unmatched ? '' : questions[0]?.text].filter(Boolean).join(' ')
    : (questions[0]?.text || '');
  return {
    empty: false,
    slots: next,
    notes,
    questions,
    question: questions[0] || null,
    reply,
    draft: buildDocument(next, context.name),
  };
}

export function calculationReady({ parameters = [], namedValues = [], accountingLines = [] } = {}) {
  const basisTerm = (namedValues || []).flatMap((row) => row.terms || []).find((term) => (
    term.kind === 'input' && ['cost_this_period', 'gross_owner_billing_period', 'revenue_this_period', 'current_contract_value', 'total_commitment_amount', 'manual_amount'].includes(term.key)
  ));
  if (!basisTerm) return false;
  if (basisTerm.key !== 'manual_amount') {
    const rate = (parameters || []).find((row) => row.key === 'rate' || row.display === 'percent' || row.display === 'per_thousand');
    if (!rate || rate.defaultValue === '' || rate.defaultValue == null) return false;
  }
  const debit = (accountingLines || []).find((line) => line.side !== 'credit');
  const credits = (accountingLines || []).filter((line) => line.side === 'credit');
  if (!debit || !credits.length) return false;
  if (debit.strategy === 'configured_account' && !debit.account_vuid) return false;
  if (credits.some((line) => !line.account_vuid && line.strategy !== 'insurance_recovery' && line.strategy !== 'accrued_insurance')) return false;
  return true;
}

export function draftFromPrompt(text, context = {}) {
  const { accounts = [], costTypes = [], name = '' } = context;
  const raw = String(text || '').trim();
  if (!raw) {
    return { notes: ['Describe what the calculation should do.'], questions: [], slots: emptySlots(), empty: true };
  }
  const result = absorb(emptySlots(), raw, { ...context, accounts, costTypes, name });
  return {
    empty: false,
    notes: result.notes,
    questions: result.questions,
    slots: result.slots,
    ...result.draft,
  };
}

export function packFromPrompt(name, text, context) {
  const draft = draftFromPrompt(text, { ...context, name });
  return {
    ...draft,
    document: documentFromState(draft.parameters || [], draft.namedValues || [], draft.source, null, draft.calculationType),
    distribution: distributionFromAccounting(draft.accountingLines || [], draft.namedValues || []),
  };
}
